import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { Button } from '@ctw/shared/components/button';
import {
  NavigationStack,
  useNavigationStackIfAvailable,
} from '@ctw/shared/components/navigation/navigation-stack';
import type { LegacyRowActionsConfigProp } from '@ctw/shared/components/tables/legacy-table';
import { useAdditionalResourceActions } from '@ctw/shared/content/resource/use-additional-resource-actions';
import { resourceDrawerActionForModel } from '@ctw/shared/context/_internal/resource/resource-drawer';
import {
  type FetchableResource,
  type FetchableResourceIdentifier,
  fetchResourceByTypeAndIds,
  useFqsHotstorageRequestOptions,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import {
  type OpenDrawerAction,
  type UseDrawerOptions,
  useDrawer,
} from '@ctw/shared/context/drawer-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import {
  type FetchableModelType,
  type FetchableResourceType,
  type ResourceTypeModelMap,
  isFetchableResourceType,
  isValidResourceId,
} from '@ctw/shared/utils/fhir/resource-mappings';
import { tw } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { QueryClient } from '@tanstack/query-core';
import { useQueryClient } from '@tanstack/react-query';
import type { GraphQLClient } from 'graphql-request';
import { isEmpty, isEqual, omit, pick } from 'lodash-es';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { SetOptional, SetRequired } from 'type-fest';

export interface ResourceDrawerState<
  TResourceType extends FetchableResourceType = FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> {
  isDrawerOpen: boolean;
  isDrawerDocked: boolean;
  isDrawerTransitioning: boolean;
  openResourceDrawer: (options: UseResourceDrawerOpenDrawerArgs<TResourceType>) => void;
  reloadResource: () => Promise<FetchableModelType<TResourceType> | null>;
  urlDeepLinkResource: FetchableResource | null;
  fetchedDeepLinkResource: TModel | null;
  setUrlDeepLinkResource: (specifier: FetchableResourceIdentifier) => void;
  configureResourceActions: (options: Parameters<typeof useAdditionalResourceActions>[0]) => void;
  resourceActionsConfig: Parameters<typeof useAdditionalResourceActions>[0];
  setDrawer: (drawer: ReturnType<typeof useDrawer>) => void;
}

const ResourceDrawerContext = createContext<ResourceDrawerState | null>(null);

interface ResourceDrawerProviderProps extends PropsWithChildren {
  enableDeepLinking?: boolean;
  onFirstViewBackButtonClick?: (args: {
    drawer: ReturnType<typeof useDrawer>;
  }) => void;
}

export const ResourceDrawerProvider = <
  TResourceType extends FetchableResourceType = FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
>({
  enableDeepLinking,
  onFirstViewBackButtonClick,
  children,
}: ResourceDrawerProviderProps) => {
  const providerTelemetry = useTelemetry();
  const { navigate, registerZuiEventListener } = useZuiContext();
  const { requestContext, graphqlClient, ctwFetch } = useCTW();
  const useRelatedBinaries = useFeatureFlag('ctw-encounter-notes-use-related-binaries');
  const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();
  const queryClient = useQueryClient();
  const [urlDeepLinkResource, internalSetUrlDeepLinkResource] = useState<FetchableResource | null>(
    null,
  );
  const [drawer, setDrawer] = useState<ReturnType<typeof useDrawer> | null>(null);
  const [resourceActionsConfig, setResourceActionsConfig] = useState<
    Parameters<typeof useAdditionalResourceActions>[0]
  >({
    rowActions: undefined,
    enableViewFhirButton: false,
    enableDismissAndReadActions: false,
    isInFooter: true,
  });
  const patientContext = usePatientContextIfAvailable();
  const [fetchedDeepLinkResource, setFetchedDeepLinkResource] = useState<TModel | null>(null);
  const canFetchDeepLinkedResource = useMemo(
    () => isEmpty(fetchedDeepLinkResource) && !isEmpty(urlDeepLinkResource) && patientContext,
    [patientContext, fetchedDeepLinkResource, urlDeepLinkResource],
  );

  const resourceActionsWithBuiltins = useAdditionalResourceActions(resourceActionsConfig);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't re-run when `navigate` changes
  const removeUrlDeepLinkResource = useCallback(() => {
    const url = new URL(globalThis.location.href);
    const urlResourceType = url.searchParams.get('resourceType') as FetchableResourceType | null;
    const urlResourceId = url.searchParams.get('resourceId');

    if (urlResourceType || urlResourceId) {
      navigate(
        pathWithParameters(
          globalThis.location.pathname,
          {
            resourceType: null,
            resourceId: null,
          },
          ['builderId', 'modalResourceId', 'modalResourceType'],
        ),
      );
    }
  }, []);

  const openResourceDrawer = useCallback(
    ({
      model,
      patient: explicitPatient,
      resourceId: explicitResourceId,
      resourceType: explicitResourceType,
      ...openDrawerArgs
    }: UseResourceDrawerOpenDrawerArgs<TResourceType>) => {
      const resourceId = (model ? model.id : explicitResourceId) ?? '';
      const resourceType = (model ? model.resourceType : explicitResourceType) ?? '';
      const patient = explicitPatient ?? patientContext?.patient;

      const url = new URL(globalThis.location.href);
      const urlResourceId = url.searchParams.get('resourceId');

      const resolvedResourceActionsWithBuiltins = resourceActionsWithBuiltins({
        record: model as never,
      });

      if (enableDeepLinking && urlResourceId !== resourceId) {
        setUrlDeepLinkResource({ resourceType, resourceId });
      }
      const effectiveTelemetry = openDrawerArgs.telemetry ?? providerTelemetry;
      effectiveTelemetry.setScopedContextProperties({
        ...openDrawerArgs.telemetryContext,
        resourceId,
        resourceType,
      });

      const performCanonicalAction = (model: TModel, patient: PatientModel) => {
        const canonicalAction =
          openDrawerArgs.action?.({
            patient,
            ctwFetch,
            graphqlClient,
            requestContext,
            telemetry: effectiveTelemetry,
            queryClient,
            closeDrawer: () => drawer?.closeDrawer(),
            model: model as never,
            reloadResource: reloadResource as never,
          }) ??
          resourceDrawerActionForModel(model)?.({
            patient,
            ctwFetch,
            graphqlClient,
            requestContext,
            telemetry: effectiveTelemetry,
            queryClient,
            closeDrawer: () => drawer?.closeDrawer(),
            model: model as never,
            reloadResource: reloadResource as never,
          });

        if (drawer && canonicalAction) {
          drawer.openDrawer({
            ...openDrawerArgs,
            action: {
              ...omit(canonicalAction, ['label', 'title', 'subtitle', 'badges']),
              telemetryContext: effectiveTelemetry.getScopedContextProperties(),
              content: {
                buttons: drawer.layout.showDeepLinkCopyButton ? (
                  <>
                    {canonicalAction.content.buttons}
                    <Button
                      testId="drawer-close-button"
                      type="button"
                      ariaLabel="Close Drawer"
                      variant="circular"
                      size="xs"
                      className={tw`flex items-center justify-center bg-transparent p-1 text-content-main shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                      onClick={close}
                    >
                      <FontAwesomeIcon icon={faCopy} className={tw`h-4 w-4`} />
                    </Button>
                  </>
                ) : (
                  canonicalAction.content.buttons
                ),
                body: (
                  <NavigationStack
                    key={`${model.resourceType}:${model.id}`}
                    telemetryContext={effectiveTelemetry.getScopedContextProperties()}
                    onFirstViewBackButtonClick={
                      onFirstViewBackButtonClick
                        ? () => onFirstViewBackButtonClick({ drawer })
                        : undefined
                    }
                    initialViews={[
                      {
                        id: `${model.resourceType}:${model.id}`,
                        label: canonicalAction.label,
                        title: canonicalAction.title,
                        subtitle: canonicalAction.subtitle,
                        badges: canonicalAction.badges,
                        content: {
                          body: () => canonicalAction.content.body,
                          footer: () =>
                            (resolvedResourceActionsWithBuiltins ||
                              canonicalAction.content.footer) && (
                              <div
                                className={tw`flex h-full w-full items-center justify-between gap-5`}
                              >
                                <div>{canonicalAction.content.footer}</div>
                                <div>
                                  {resourceActionsWithBuiltins({
                                    record: model as never,
                                  })}
                                </div>
                              </div>
                            ),
                        },
                      },
                    ]}
                  />
                ),
              },
            },
          });
        }
      };

      if (model && patient) {
        performCanonicalAction(model as never, patient);
      } else if (patient) {
        void fetchResourceByTypeAndIds({
          resourceType: resourceType as never,
          resourceIds: [resourceId],
          ctwFetch,
          graphqlClient,
          requestContext,
          queryClient,
          patient,
          telemetry: effectiveTelemetry,
          includeClinicalNotes: !useRelatedBinaries,
          fqsHotstorageRequestOptions,
        }).then((fetchedResources) => {
          performCanonicalAction(fetchedResources[0], patient);
        });
      } else {
        effectiveTelemetry.trackError({
          error: new Error('Cannot open resource drawer: patient context required'),
          context: { resourceId, resourceType },
        });
        throw new Error(
          `Resource drawer requires patient context. ResourceType: ${resourceType}, ResourceId: ${resourceId}`,
        );
      }
    },
    [
      drawer,
      resourceActionsWithBuiltins,
      ctwFetch,
      graphqlClient,
      requestContext,
      queryClient,
      providerTelemetry,
      patientContext,
      enableDeepLinking,
      onFirstViewBackButtonClick,
      useRelatedBinaries,
      fqsHotstorageRequestOptions,
    ],
  );

  const fetchResource = useCallback(async (): Promise<TModel | null> => {
    if (!(canFetchDeepLinkedResource && urlDeepLinkResource?.resourceId)) {
      return null;
    }

    const reloadedResource = patientContext?.patient
      ? (
          await fetchResourceByTypeAndIds({
            telemetry: providerTelemetry,
            ctwFetch,
            graphqlClient,
            requestContext,
            queryClient,
            patient: patientContext?.patient,
            resourceType: urlDeepLinkResource.resourceType,
            resourceIds: [urlDeepLinkResource.resourceId],
            includeClinicalNotes: !useRelatedBinaries,
            fqsHotstorageRequestOptions,
          })
        )[0]
      : null;

    return reloadedResource as never;
  }, [
    canFetchDeepLinkedResource,
    providerTelemetry,
    patientContext,
    ctwFetch,
    graphqlClient,
    requestContext,
    queryClient,
    urlDeepLinkResource?.resourceId,
    urlDeepLinkResource?.resourceType,
    useRelatedBinaries,
    fqsHotstorageRequestOptions,
  ]);

  const reloadResource = useCallback(async () => {
    const fetchedResource = await fetchResource();

    setFetchedDeepLinkResource(fetchedResource);

    if (fetchedResource) {
      openResourceDrawer({
        telemetry: providerTelemetry,
        model: fetchedResource,
        reloadResource,
      });
    }

    return fetchedResource;
  }, [fetchResource, providerTelemetry, openResourceDrawer]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't re-run when `navigate` changes
  const setUrlDeepLinkResource = useCallback(
    ({ resourceType, resourceId }: FetchableResourceIdentifier) => {
      if (
        isFetchableResourceType(resourceType) &&
        isValidResourceId(resourceId) &&
        resourceId !== urlDeepLinkResource?.resourceId
      ) {
        navigate(
          pathWithParameters(
            globalThis.location.pathname,
            {
              resourceType,
              resourceId,
            },
            ['builderId', 'modalResourceId', 'modalResourceType'],
          ),
        );
        internalSetUrlDeepLinkResource({ resourceType, resourceId });
      }
    },
    [reloadResource, urlDeepLinkResource],
  );

  useEffect(() => {
    const url = new URL(globalThis.location.href);
    const urlResourceType = url.searchParams.get('resourceType') as FetchableResourceType | null;
    const urlResourceId = url.searchParams.get('resourceId');

    if (enableDeepLinking && urlResourceType !== null && urlResourceId !== null) {
      internalSetUrlDeepLinkResource({ resourceType: urlResourceType, resourceId: urlResourceId });
    }
  }, [enableDeepLinking]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No deps change after initial render
  useEffect(
    () =>
      registerZuiEventListener('afterUrlChange', () => {
        requestAnimationFrame(() => {
          const url = new URL(globalThis.location.href);
          const urlResourceId = url.searchParams.get('resourceId');
          if (!isEmpty(urlResourceId)) {
            setFetchedDeepLinkResource(null);
          }
        });
      }),
    [],
  );

  useEffect(() => {
    if (
      urlDeepLinkResource?.resourceId &&
      (!fetchedDeepLinkResource?.id ||
        fetchedDeepLinkResource.id !== urlDeepLinkResource.resourceId)
    ) {
      void fetchResource().then((fetchedResource) => {
        if (fetchedResource) {
          setFetchedDeepLinkResource(fetchedResource);
          openResourceDrawer({
            telemetry: providerTelemetry,
            model: fetchedResource,
            reloadResource,
          });
        }
      });
    }
  }, [
    urlDeepLinkResource,
    fetchResource,
    fetchedDeepLinkResource,
    openResourceDrawer,
    reloadResource,
    providerTelemetry,
  ]);

  return (
    <ResourceDrawerContext.Provider
      value={{
        isDrawerOpen: drawer?.isDrawerOpen ?? false,
        isDrawerDocked: drawer?.isDrawerDocked ?? false,
        isDrawerTransitioning: drawer?.isDrawerTransitioning ?? false,
        openResourceDrawer: openResourceDrawer as never,
        reloadResource,
        urlDeepLinkResource,
        setUrlDeepLinkResource,
        fetchedDeepLinkResource,
        configureResourceActions: setResourceActionsConfig,
        resourceActionsConfig,
        setDrawer: (newDrawer) => {
          if (!drawer) {
            newDrawer.onDrawerEvent('ResourceDrawer', (eventType) => {
              if (eventType === 'close') {
                requestAnimationFrame(() => removeUrlDeepLinkResource());
              }
            });
            setDrawer(newDrawer);
            void reloadResource();
          }
        },
      }}
    >
      {children}
    </ResourceDrawerContext.Provider>
  );
};

export interface OpenResourceDrawerAction
  extends SetRequired<OpenDrawerAction, 'label' | 'title'> {}

interface UseResourceDrawerCommonOpenDrawerArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends SetOptional<Omit<OpenDrawerAction, 'allowDocking'>, 'content'> {
  enableViewFhirButton?: boolean;
  enableDismissAndReadActions?: boolean;
  telemetry: ReturnType<typeof useTelemetry>;
  telemetryContext?: TelemetryContext;
  reloadResource?: () => Promise<TModel | null>;
  patient?: PatientModel;
  action?: (args: UseResourceDrawerActionArgs<TResourceType, TModel>) => OpenResourceDrawerAction;
}

interface ResourceDrawerPushViewArgs extends FetchableResourceIdentifier {
  patient?: PatientModel;
}

interface UseResourceDrawerOpenWithModelDrawerArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends UseResourceDrawerCommonOpenDrawerArgs<TResourceType, TModel> {
  model: TModel;
  resourceId?: never;
  resourceType?: never;
}

interface UseResourceDrawerOpenWithResourceTypeAndIdDrawerArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends UseResourceDrawerCommonOpenDrawerArgs<TResourceType, TModel> {
  resourceId: string;
  resourceType: string;
  model?: never;
}

export type UseResourceDrawerOpenDrawerArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> =
  | UseResourceDrawerOpenWithModelDrawerArgs<TResourceType, TModel>
  | UseResourceDrawerOpenWithResourceTypeAndIdDrawerArgs<TResourceType, TModel>;

export interface UseResourceDrawerActionArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType>,
> {
  ctwFetch: CTWState['ctwFetch'];
  graphqlClient: GraphQLClient;
  requestContext: CTWRequestContext;
  telemetry: ReturnType<typeof useTelemetry>;
  queryClient: QueryClient;
  model: TModel;
  patient: PatientModel;
  closeDrawer: () => void;
  reloadResource: () => Promise<TModel | null>;
}

export interface UseResourceDrawerOptions<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends Omit<UseDrawerOptions, 'boundaryName'> {
  enableViewFhirButton?: boolean;
  enableDismissAndReadActions?: boolean;
  resourceActions?: TResourceType extends keyof ResourceTypeModelMap
    ? (model: TModel) => LegacyRowActionsConfigProp<TModel>
    : never;
  action?: (args: UseResourceDrawerActionArgs<TResourceType, TModel>) => OpenResourceDrawerAction;
}

const RESOURCE_ACTION_EQUALITY_SCALAR_PROPERTIES = [
  'isInFooter',
  'enableViewFhirButton',
  'enableDismissAndReadActions',
] as const;

export const useResourceDrawer = <TResourceType extends FetchableResourceType>(
  {
    telemetryContext: hookExplicitTelemetryContext,
    allowDocking = true,
    onDrawerEvent,
    layout,
    enableViewFhirButton = true,
    enableDismissAndReadActions = false,
    resourceActions,
    action,
  }: UseResourceDrawerOptions<TResourceType> = {
    enableViewFhirButton: true,
    enableDismissAndReadActions: false,
  },
) => {
  const context = useContext(ResourceDrawerContext);
  const useRelatedBinariesForPushView = useFeatureFlag('ctw-encounter-notes-use-related-binaries');
  const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();

  if (!context) {
    throw new Error('useResourceDrawer must be used inside a ResourceDrawerContext');
  }

  const navigationStack = useNavigationStackIfAvailable();
  const { requestContext, graphqlClient, ctwFetch } = useCTW();
  const patientContext = usePatientContextIfAvailable();
  const hookLocalTelemetry = useTelemetry();

  const drawer = useDrawer({
    boundaryName: 'ResourceDrawer',
    telemetryContext: {
      ...hookLocalTelemetry.getScopedContextProperties(),
      ...hookExplicitTelemetryContext,
      withinResourceDrawer: true,
    },
    allowDocking,
    onDrawerEvent,
    layout,
  });

  const queryClient = useQueryClient();

  const onMutate = useCallback(async () => {
    // Invalidate patient data queries so lists behind the drawer reflect
    // changes made to the resource (e.g., writeback, toggle-read, edit form).
    // `invalidateQueries` marks matching queries stale — React Query refetches
    // active observers in the background without forcing simultaneous fetches.
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const queryId = query.queryKey[0];
        if (typeof queryId !== 'string') {
          return false;
        }
        // Patient-scoped query keys are `patient` or `patient_*` (underscore,
        // not dot). `document_references_for_binary_ids` is a non-patient-
        // prefixed key used by the encounter source-documents hook — its
        // `upid` scoping is in the queryKey payload rather than the queryId
        // prefix, so we match it explicitly.
        return (
          queryId === 'patient' ||
          queryId.startsWith('patient_') ||
          queryId === 'document_references_for_binary_ids'
        );
      },
    });
    await context.reloadResource();
  }, [queryClient, context.reloadResource]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't change these options after mount
  useEffect(() => {
    context.setDrawer(drawer);

    const canonicalResourceActionsConfig = {
      rowActions: resourceActions as never,
      enableViewFhirButton,
      enableDismissAndReadActions,
      isInFooter: true,
      onWritebackEnd: onMutate,
      onToggleRead: onMutate,
    };

    // Calling `context.configureResourceActions` causes a re-render, so we need to make sure we
    // only do so when the configuration has functionally changed.
    if (
      !isEqual(
        pick(canonicalResourceActionsConfig, RESOURCE_ACTION_EQUALITY_SCALAR_PROPERTIES),
        pick(context.resourceActionsConfig, RESOURCE_ACTION_EQUALITY_SCALAR_PROPERTIES),
      ) ||
      typeof canonicalResourceActionsConfig.rowActions !==
        typeof context.resourceActionsConfig.rowActions
    ) {
      requestAnimationFrame(() => {
        context.configureResourceActions({
          rowActions: resourceActions as never,
          enableViewFhirButton,
          enableDismissAndReadActions,
          isInFooter: true,
          onWritebackEnd: onMutate,
          onToggleRead: onMutate,
        });
      });
    }
  }, [drawer, enableDismissAndReadActions, enableViewFhirButton, context.resourceActionsConfig]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't change miissing deps after mount
  const openResourceDrawer = useCallback(
    (options: UseResourceDrawerOpenDrawerArgs<TResourceType>) => {
      const resourceId = options?.model?.id ?? options.resourceId;
      const resourceType = options?.model?.resourceType ?? options.resourceType;

      requestAnimationFrame(() => {
        context.openResourceDrawer({
          action,
          enableViewFhirButton,
          enableDismissAndReadActions,
          ...options,
          telemetryContext: {
            ...hookLocalTelemetry.getScopedContextProperties(),
            ...hookExplicitTelemetryContext,
            ...options.telemetryContext,
            resourceId,
            resourceType,
            withinResourceDrawer: true,
          },
        } as never);
      });
    },
    [drawer, action, context, enableDismissAndReadActions, enableViewFhirButton],
  );

  const pushView = useCallback(
    ({ resourceType, resourceId, patient: explicitPatient }: ResourceDrawerPushViewArgs) => {
      const patient = explicitPatient ?? patientContext?.patient;
      if (navigationStack && patient && isFetchableResourceType(resourceType)) {
        void fetchResourceByTypeAndIds({
          resourceType,
          resourceIds: [resourceId],
          ctwFetch,
          graphqlClient,
          requestContext,
          queryClient,
          patient,
          telemetry: hookLocalTelemetry,
          includeClinicalNotes: !useRelatedBinariesForPushView,
          fqsHotstorageRequestOptions,
        }).then((fetchedResources) => {
          const fetchedResource = fetchedResources[0];

          if (fetchedResource) {
            const navigationStackViewId = `${fetchedResource.resourceType}:${fetchedResource.id}`;
            const resourceDrawerAction = resourceDrawerActionForModel(fetchedResource);

            if (resourceDrawerAction) {
              const resourceDrawerActionForModel = resourceDrawerAction({
                patient,
                ctwFetch,
                graphqlClient,
                requestContext,
                telemetry: hookLocalTelemetry,
                queryClient,
                closeDrawer: () => drawer?.closeDrawer(),
                model: fetchedResource as never,
                reloadResource: (async () => {
                  const reloadedResources = await fetchResourceByTypeAndIds({
                    resourceType,
                    resourceIds: [resourceId],
                    ctwFetch,
                    graphqlClient,
                    requestContext,
                    queryClient,
                    patient,
                    telemetry: hookLocalTelemetry,
                    includeClinicalNotes: !useRelatedBinariesForPushView,
                    fqsHotstorageRequestOptions,
                  });
                  const reloadedResource = reloadedResources[0];

                  navigationStack.replaceView(navigationStackViewId, {
                    id: navigationStackViewId,
                    ...resourceDrawerActionForModel,
                    content: {
                      body: () => resourceDrawerActionForModel.content.body,
                      footer: resourceDrawerActionForModel.content.footer
                        ? () => resourceDrawerActionForModel.content.footer
                        : undefined,
                    },
                  });

                  return reloadedResource;
                }) as never,
              });

              navigationStack.pushView({
                id: navigationStackViewId,
                ...resourceDrawerActionForModel,
                content: {
                  body: () => resourceDrawerActionForModel.content.body,
                  footer: resourceDrawerActionForModel.content.footer
                    ? () => resourceDrawerActionForModel.content.footer
                    : undefined,
                },
              });
            }
          }
        });
      }
    },
    [
      navigationStack,
      ctwFetch,
      graphqlClient,
      requestContext,
      queryClient,
      hookLocalTelemetry,
      patientContext,
      drawer?.closeDrawer,
      useRelatedBinariesForPushView,
      fqsHotstorageRequestOptions,
    ],
  );

  const popView = useCallback(() => {
    if (navigationStack) {
      navigationStack.popView();
    }
  }, [navigationStack]);

  return {
    setUrlDeepLinkResource: context.setUrlDeepLinkResource,
    openResourceDrawer,
    closeResourceDrawer: drawer.closeDrawer,
    pushView,
    popView,
  };
};
